import { QueryClient } from '@tanstack/react-query';
import { createTRPCProxyClient, httpBatchLink } from '@trpc/client';
import { createTRPCReact } from '@trpc/react-query';
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '../../../backend/src/trpc/router';
import { buildInfoHeaders } from './build-info';

export const trpc = createTRPCReact<AppRouter>();

export type RouterInputs = inferRouterInputs<AppRouter>;
export type RouterOutputs = inferRouterOutputs<AppRouter>;

export const queryClient = new QueryClient();

/**
 * Call on sign-out, alongside clearing the auth token cache (`clearBetterAuthTokenCache` from
 * `./betterAuthClient`). Without this, a newly-signed-in different account can render the
 * previous account's cached query results and workspace selection before its own requests
 * resolve. Mirrors `extension/utils/purgeState.ts`'s `purgeAllExtensionState` for the web portals.
 */
export function purgeWorkspaceLocalState(): void {
  localStorage.removeItem('workspaceId');
  localStorage.removeItem('organizationId');
  queryClient.clear();
}

/** Resolves the active workspace id the same way the tRPC link headers do (URL first, then localStorage). */
export function getActiveWorkspaceId(): number | null {
  const match = window.location.pathname.match(/\/workspace\/(\d+)/);
  if (match) return parseInt(match[1], 10);
  const stored = localStorage.getItem('workspaceId');
  return stored ? parseInt(stored, 10) : null;
}

/** Base HTTP(S) URL of the backend worker, derived the same way the tRPC link does. */
export function getWorkerUrl(): string {
  return import.meta.env.VITE_WORKER_URL;
}

type TokenGetter = () => Promise<string | null>;

/**
 * Every consumer installs its own getter at startup — both portals in `src/main.tsx` and the
 * extension in `extension/components/Providers.tsx`, all before the first render. There is no
 * default provider on purpose: this used to fall back to Firebase via a lazy `import('./firebase')`,
 * and even though no app had reached that branch since the Better Auth migration, the static edge
 * dragged the whole `firebase/auth` SDK into every bundle. In the extension that meant shipping
 * its remotely-hosted-code URLs, which got the MV3 listing rejected.
 */
let activeTokenGetter: TokenGetter = async () => null;

export function setAuthTokenGetter(getter: TokenGetter) {
  activeTokenGetter = getter;
}

/**
 * Exported for the handful of calls that cannot go through tRPC — currently the
 * avatar upload, which moves raw image bytes. Anything hitting the backend
 * outside tRPC must reuse this rather than re-deriving the token and workspace,
 * so the injected getter above stays the single switch point.
 */
export async function buildAuthHeaders(): Promise<Record<string, string>> {
  // Resolve workspaceId from URL first (source of truth), then fallback to localStorage
  let workspaceId: string | null = null;
  const match = window.location.pathname.match(/\/workspace\/(\d+)/);
  if (match) {
    workspaceId = match[1];
  } else {
    workspaceId = localStorage.getItem('workspaceId');
  }

  const headers: Record<string, string> = { ...buildInfoHeaders() };

  const token = await activeTokenGetter();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  if (workspaceId) {
    headers['x-workspace-id'] = workspaceId;
  }

  return headers;
}

export const trpcReactClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: `${import.meta.env.VITE_WORKER_URL}/trpc`,
      headers: buildAuthHeaders,
    }),
  ],
});

/**
 * Vanilla tRPC client for use outside of React components
 */
export const trpcClient = createTRPCProxyClient<AppRouter>({
  links: [
    httpBatchLink({
      url: `${import.meta.env.VITE_WORKER_URL}/trpc`,
      headers: buildAuthHeaders,
    }),
  ],
});
